import { CONTACT_EMAIL } from "@/lib/apps";

export const DELETE_UPDATED = { en: "October 2026", da: "Oktober 2026" };

const en = [
  {
    id: "how",
    h: "How to request deletion",
    p: ["You can ask ZenVio to delete your account and all associated data for ZenSeo, ZenSay, ZenSync or ZenGage at any time. You do not need to log in or have the app installed."],
    list: [
      "Fill in the request form on this page with the e-mail address linked to your account, and choose the app (or all apps).",
      `Or send an e-mail to ${CONTACT_EMAIL} with the subject "Delete my account", the name of the app and the e-mail address you use in the app.`,
      "Before we delete anything, we may e-mail you to confirm that the request comes from the account owner.",
      "We complete the deletion within 30 days and send you a confirmation by e-mail when it is done.",
    ],
    after: ["Uninstalling an app does not delete your account. Subscriptions are managed by Google Play or the App Store, so please cancel any active subscription there to stop future charges."],
  },
  { id: "request", h: "Request form", form: true },
  {
    id: "deleted",
    h: "What is permanently deleted",
    p: ["When your request has been completed, the following data is permanently deleted and cannot be recovered:"],
    list: [
      "Your account and profile details: name, e-mail address, account ID, preferences and settings.",
      "Everything you created or saved in the app, for example websites, scans and SEO reports in ZenSeo; messages, reminders, chats and memories in ZenSay; notes, files, photos, voice notes and collections in ZenSync; brand details, drafts, posts and schedules in ZenGage.",
      "Connections to third-party accounts (such as Google Search Console, Google Analytics or social media accounts) and the access tokens we hold for them.",
      "AI conversation history and personalisation data.",
      "Device identifiers, notification tokens and usage data linked to your account.",
      "If you ask us to, your e-mail address on ZenVio launch lists and the messages you have sent us through the contact form.",
    ],
  },
  {
    id: "retained",
    h: "Data we may need to keep",
    p: ["Some data may be kept for a limited time when the law requires it. It is used for nothing else and is deleted when the period ends:"],
    list: [
      "Accounting records: if you have made a purchase, records of the transaction (date, amount, product and invoice number) are kept for 5 years from the end of the financial year they relate to, as required by the Danish Bookkeeping Act (bogføringsloven). Card and payment details are handled by Google Play or Apple and are never stored by ZenVio.",
      "Backups: deleted data may remain in encrypted backups for up to 30 days, after which it is overwritten automatically.",
      "Legal obligations: if we must keep specific data to comply with a legal obligation or a court order, or to establish or defend legal claims, we keep only that data and only for as long as necessary.",
      "Proof of deletion: a record of your request (e-mail address, app and date) is kept for 12 months to document that we handled it.",
      "Anonymous statistics: aggregated data that can no longer identify you (such as anonymous crash reports) may be kept, as it is no longer personal data.",
    ],
  },
  {
    id: "contact",
    h: "Questions and support",
    p: [
      `If you have questions about deleting your account or your data, contact ZenVio support at ${CONTACT_EMAIL} or use the contact form on www.zenvio.dk. We usually reply within a few working days.`,
      "If you are not satisfied with how we handled your request, you have the right to complain to the Danish Data Protection Agency (Datatilsynet), www.datatilsynet.dk.",
      "Developer: ZenVio, Denmark · www.zenvio.dk",
    ],
  },
];

const da = [
  {
    id: "how",
    h: "Sådan anmoder du om sletning",
    p: ["Du kan til enhver tid bede ZenVio om at slette din konto og alle tilknyttede data for ZenSeo, ZenSay, ZenSync eller ZenGage. Du behøver ikke at logge ind eller have appen installeret."],
    list: [
      "Udfyld formularen på denne side med den e-mailadresse, der er knyttet til din konto, og vælg appen (eller alle apps).",
      `Eller send en e-mail til ${CONTACT_EMAIL} med emnet "Slet min konto", appens navn og den e-mailadresse, du bruger i appen.`,
      "Før vi sletter noget, kan vi sende dig en e-mail for at bekræfte, at anmodningen kommer fra kontoens ejer.",
      "Vi gennemfører sletningen inden for 30 dage og sender dig en bekræftelse på e-mail, når den er udført.",
    ],
    after: ["Hvis du afinstallerer en app, slettes din konto ikke. Abonnementer administreres af Google Play eller App Store, så opsig venligst et aktivt abonnement dér for at undgå fremtidige betalinger."],
  },
  { id: "request", h: "Anmodningsformular", form: true },
  {
    id: "deleted",
    h: "Hvad der slettes permanent",
    p: ["Når din anmodning er gennemført, slettes følgende data permanent og kan ikke gendannes:"],
    list: [
      "Din konto og dine profiloplysninger: navn, e-mailadresse, konto-ID, præferencer og indstillinger.",
      "Alt, hvad du har oprettet eller gemt i appen, for eksempel hjemmesider, scanninger og SEO-rapporter i ZenSeo; beskeder, påmindelser, chats og minder i ZenSay; noter, filer, fotos, stemmenoter og samlinger i ZenSync; brandoplysninger, kladder, opslag og planer i ZenGage.",
      "Forbindelser til tredjepartskonti (såsom Google Search Console, Google Analytics eller sociale medier) og de adgangstokens, vi opbevarer til dem.",
      "AI-samtalehistorik og personaliseringsdata.",
      "Enheds-ID'er, notifikationstokens og brugsdata knyttet til din konto.",
      "Hvis du beder om det, din e-mailadresse på ZenVios lanceringslister og de beskeder, du har sendt os via kontaktformularen.",
    ],
  },
  {
    id: "retained",
    h: "Data vi kan være forpligtet til at gemme",
    p: ["Visse data kan blive opbevaret i en begrænset periode, når loven kræver det. De bruges ikke til andet og slettes, når perioden udløber:"],
    list: [
      "Regnskabsmateriale: hvis du har foretaget et køb, opbevares oplysninger om transaktionen (dato, beløb, produkt og fakturanummer) i 5 år fra udgangen af det regnskabsår, de vedrører, som krævet af bogføringsloven. Kort- og betalingsoplysninger håndteres af Google Play eller Apple og opbevares aldrig af ZenVio.",
      "Sikkerhedskopier: slettede data kan forblive i krypterede sikkerhedskopier i op til 30 dage, hvorefter de automatisk overskrives.",
      "Juridiske forpligtelser: hvis vi skal opbevare bestemte data for at overholde en retlig forpligtelse eller en retskendelse, eller for at fastlægge eller forsvare retskrav, opbevarer vi kun disse data og kun så længe, det er nødvendigt.",
      "Dokumentation for sletning: en registrering af din anmodning (e-mailadresse, app og dato) opbevares i 12 måneder som dokumentation for, at vi har håndteret den.",
      "Anonym statistik: samlede data, der ikke længere kan identificere dig (såsom anonyme nedbrudsrapporter), kan opbevares, da de ikke længere er personoplysninger.",
    ],
  },
  {
    id: "contact",
    h: "Spørgsmål og support",
    p: [
      `Har du spørgsmål om sletning af din konto eller dine data, kan du kontakte ZenVio support på ${CONTACT_EMAIL} eller bruge kontaktformularen på www.zenvio.dk. Vi svarer normalt inden for få hverdage.`,
      "Hvis du ikke er tilfreds med, hvordan vi har håndteret din anmodning, har du ret til at klage til Datatilsynet, www.datatilsynet.dk.",
      "Udvikler: ZenVio, Danmark · www.zenvio.dk",
    ],
  },
];

export const deleteSections = { en, da };

export const deleteText = {
  en: {
    eyebrow: "Account & data",
    title: "Delete Account",
    sub: "How to delete your ZenSeo, ZenSay, ZenSync or ZenGage account and all associated data. No login required.",
    updated: "Last updated",
    toc: "Contents",
    email: "E-mail address linked to your account",
    app: "Which app?",
    all: "All apps",
    message: "Anything we should know? (optional)",
    confirm: "I understand that deletion is permanent and cannot be undone.",
    submit: "Request deletion",
    sending: "Sending…",
    invalid: "Please enter a valid e-mail address and tick the confirmation box.",
    error: "Something went wrong. Please try again, or e-mail us instead.",
    successTitle: "Request received",
    successSub: "We'll verify your request and delete your account and data within 30 days. You'll get a confirmation by e-mail when it's done.",
    another: "Submit another request",
  },
  da: {
    eyebrow: "Konto og data",
    title: "Slet konto",
    sub: "Sådan sletter du din konto til ZenSeo, ZenSay, ZenSync eller ZenGage og alle tilknyttede data. Login er ikke nødvendigt.",
    updated: "Senest opdateret",
    toc: "Indhold",
    email: "E-mailadresse knyttet til din konto",
    app: "Hvilken app?",
    all: "Alle apps",
    message: "Er der noget, vi skal vide? (valgfrit)",
    confirm: "Jeg forstår, at sletningen er permanent og ikke kan fortrydes.",
    submit: "Anmod om sletning",
    sending: "Sender…",
    invalid: "Indtast en gyldig e-mailadresse, og sæt flueben i bekræftelsesfeltet.",
    error: "Noget gik galt. Prøv igen, eller send os en e-mail i stedet.",
    successTitle: "Anmodning modtaget",
    successSub: "Vi bekræfter din anmodning og sletter din konto og dine data inden for 30 dage. Du får en bekræftelse på e-mail, når det er gjort.",
    another: "Send en ny anmodning",
  },
};
